import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissionForm, addSubmissionForm, updateSubmissionForm, clearCurrentSubmissionForm } from '../actions/submissionFormActions';
import SubmissionFormForm from '../components/content-management/SubmissionFormForm';

import SubmissionFormFormSkeleton from '../components/content-management/views/FormView/SubmissionFormFormSkeleton';
import AdminLayout from '../components/settings/AdminLayout';
import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const SubmissionFormEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentSubmissionForm, submissionFormLoading } = useSelector(state => state.submissionForm);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    const loadData = async () => {
      try {
        // If we have an ID, load the submission form
        if (id) {
          await dispatch(getSubmissionForm(id));
        }
      } catch (error) {
        console.error('Error loading submission form:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentSubmissionForm());
    };
  }, [dispatch, id]);

  const handleSubmit = async (formData) => {
    try {
      const action = id ? updateSubmissionForm(id, formData) : addSubmissionForm(formData);
      await dispatch(action);
      // Redirect back to submission forms list
      navigate('/settings', { state: { activeTab: 'Submission Forms' } });
    } catch (error) {
      console.error('Error saving submission form:', error);
    }
  };

  const handleClose = () => {
    navigate('/settings', { state: { activeTab: 'Submission Forms' } });
  };

  // Show loading state
  if (submissionFormLoading && id) {
    return (
      <AdminLayout activeTab="Submission Forms">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
            <button
              type="button"
              onClick={() => navigate('/settings', { state: { activeTab: 'Submission Forms' } })}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form Skeleton */}
          <SubmissionFormFormSkeleton />
        </div>
      </AdminLayout>
    );
  }

  // If trying to edit/view a submission form that doesn't exist
  if (id && !currentSubmissionForm && !submissionFormLoading) {
    return (
      <AdminLayout activeTab="Submission Forms">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-500 text-xl mb-4">Submission Form not found</div>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Submission Forms' } })}
            className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            Return to Submission Forms
          </button>
        </div>
      </AdminLayout>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <AdminLayout activeTab="Submission Forms">
      <SubmissionFormForm
        mode={mode}
        submissionForm={currentSubmissionForm}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </AdminLayout>
  );
};

export default SubmissionFormEditor;
